import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Crown, Loader2, Gift, Calendar, Moon } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import API_BASE_URL from '@/utils/apiUrl';

const BACKEND_URL = API_BASE_URL;

export const VIPSubscription = ({ userData, onVIPActivated }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Prašome įvesti teisingą el. pašto adresą');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/vip-subscribe`, {
        email: email,
        firstName: userData?.firstName || '',
        lifePath: userData?.lifePath || 0
      });
      
      if (response.data.success) {
        setSubscribed(true);
        toast.success(response.data.message);
        setEmail('');
        
        // Notify parent component to unlock premium content
        if (onVIPActivated) {
          onVIPActivated(email);
        }
        
        // Store VIP status in localStorage
        localStorage.setItem('vipStatus', JSON.stringify({
          email: email,
          activated: new Date().toISOString()
        }));
      }
    } catch (error) {
      console.error('VIP subscription error:', error);
      toast.error('Įvyko klaida. Bandykite dar kartą.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="card-mystical p-8 border-primary/30 shadow-glow relative overflow-hidden">
      {/* Decorative Crown Icon */}
      <div className="absolute top-4 right-4 opacity-10">
        <Crown className="w-32 h-32 text-primary" />
      </div>
      
      <div className="relative z-10">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center gap-2 mb-4">
            <Crown className="w-10 h-10 text-primary animate-pulse-glow" />
            <h3 className="font-display text-2xl sm:text-3xl text-primary">
              Noriu matyti visą paveikslą
            </h3>
          </div>
          <p className="text-foreground/90 text-lg font-semibold mb-2">
            Gauk viską tik už <span className="text-primary text-2xl">€2.99/mėn.</span>
          </p>
          <p className="text-xs text-muted-foreground">
            ⚡ FIRST TIME DEAL – Specialus pasiūlymas naujiems nariams
          </p>
        </div>

        {/* Benefits */}
        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-4 bg-secondary/20 border border-primary/20 rounded-lg p-4">
            <Gift className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-foreground mb-1">🎁 DOVANA: Sielos žemėlapio archyvas</h4>
              <p className="text-sm text-foreground/80">Tai giliausia tavo asmenybės ir likimo matrica. Tavo asmeninė Sielos Knyga, suformuota į vieną PDF dokumentą, kuriame skaičiai atskleidžia tavo prigimtį, stiprybes ir šio gyvenimo kelio užduotis.</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 bg-secondary/20 border border-primary/20 rounded-lg p-4">
            <Calendar className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-foreground mb-1">📅 SAVAITĖS PROGNOZĖ</h4>
              <p className="text-sm text-foreground/80">Kiekvieną sekmadienį tavo asmeninė prognozė ateinančiai savaitei</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4 bg-secondary/20 border border-primary/20 rounded-lg p-4">
            <Moon className="w-6 h-6 text-primary flex-shrink-0 mt-1" />
            <div>
              <h4 className="font-semibold text-foreground mb-1">🌙 MĖNESIO APŽVALGA</h4>
              <p className="text-sm text-foreground/80">Kiekvieno mėnesio pradžioje tavo mėnesio tendencijų apžvalga</p>
            </div>
          </div>
        </div>

        {!subscribed ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jusu@elpastas.lt"
                className="bg-input border-border text-foreground placeholder:text-muted-foreground"
                disabled={loading}
                required
              />
            </div>
            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary-glow text-primary-foreground font-semibold py-6 text-lg glow-gold"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Siunčiama...
                </>
              ) : (
                <>
                  <Crown className="w-5 h-5 mr-2" />
                  Tapti VIP nariu dabar
                </>
              )}
            </Button>
          </form>
        ) : (
          <div className="bg-primary/20 border border-primary rounded-lg p-6 text-center">
            <Crown className="w-12 h-12 text-primary mx-auto mb-3" />
            <p className="text-primary font-semibold text-xl mb-2">
              ✓ Sveikiname! Esate VIP narys
            </p>
            <p className="text-foreground/80">
              Visos premium prognozės dabar atrakintos! Tikrinkite savo el. paštą dėl PDF analizės!
            </p>
          </div>
        )}

        <p className="text-center text-muted-foreground text-xs mt-4">
          * Prenumerata atsinaujina automatiškai. Galite atšaukti bet kada.
        </p>
      </div>
    </Card>
  );
};